/**
 * Navigation host-rebasing — conversation continuity across agent-
 * driven navs.
 *
 * The agent mints URLs from its ingested page list, which routinely
 * carries the OTHER host variant of the customer's site (`www.x.com`
 * vs `x.com` — KBs hold both). Navigating to the other variant is
 * "the same site" to a human but a DIFFERENT ORIGIN to the browser:
 * sessionStorage doesn't cross, so the next page's widget finds no
 * snapshot and cold-mounts at the page default — the visitor's open
 * conversation silently vanishes (live prod catch 2026-07-13). The
 * strict-origin sp:nav path was worse: it classified the variant as
 * cross-origin and opened a NEW TAB.
 *
 * Rule: before any agent-driven navigation, rebase a same-SITE target
 * onto the visitor's CURRENT protocol+host. Same page content (that's
 * the premise of the www-tolerant sameSite check), same origin — the
 * session snapshot survives and the rail resumes.
 */

/**
 * Same-site check that tolerates the `www.` vs bare-host distinction.
 * Strict origin comparison rejects `https://showpilot.com/x` when the
 * visitor is on `https://www.showpilot.com/y` (or the reverse), which
 * silently breaks legitimate bot-driven navigations.
 */
export function sameSite(a: URL | Location, b: URL | Location): boolean {
  const norm = (h: string) => h.toLowerCase().replace(/^www\./, '');
  return a.protocol === b.protocol && norm(a.hostname) === norm(b.hostname);
}

/**
 * Rebase a same-site target URL onto the visitor's current origin.
 * Returns the (possibly rewritten) URL when the target is same-site,
 * or null when it's genuinely cross-site (different apex domain or
 * non-www subdomain — `app.x.com` vs `x.com` is NOT rebased).
 */
export function rebaseToVisitorOrigin(target: URL, loc: URL | Location): URL | null {
  if (!sameSite(target, loc)) return null;
  if (target.host === loc.host && target.protocol === loc.protocol) return target;
  const rebased = new URL(target.href);
  rebased.protocol = loc.protocol;
  rebased.host = loc.host;
  return rebased;
}
